import React, { useCallback, useEffect, useState } from 'react'
import { Archive, Copy, LayoutTemplate, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Button } from '@/components/ui/button'
import { PlanCard } from '@/components/PlanCard'
import TemplateEditor from './TemplateEditor'
import { ARCHIVE_TEMPLATE, DELETE_TEMPLATE, DUPLICATE_TEMPLATE, GET_TEMPLATES, SAMPLE_PLANS, serverError, type PlanCardTemplate } from './editing'

/**
 * Plan cards: the designs the paywall draws plans with. A global dashboard view rather
 * than an organisation tab, because one template serves plans of any B2C organisation.
 * Assigning a template to a plan happens on the organisation's Plans tab.
 */

const VARIANT_NAMES: Record<string, string> = { default: 'Default', featured: 'Featured', muted: 'Muted' }

const PlanCardsView: React.FC = () => {
  const [templates, setTemplates] = useState<PlanCardTemplate[]>([])
  const [showArchived, setShowArchived] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  /** The template being edited; 'new' for a new one. */
  const [editing, setEditing] = useState<PlanCardTemplate | 'new' | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const response = await authorizedGraphQLRequest<{ getPlanCardTemplates: PlanCardTemplate[] }>(GET_TEMPLATES, { includeArchived: showArchived })
      setTemplates(response.getPlanCardTemplates || [])
    } catch (err) {
      setError(serverError(err, 'Failed to load templates').message)
    } finally {
      setLoading(false)
    }
  }, [showArchived])

  useEffect(() => {
    load()
  }, [load])

  const run = async (id: number, action: () => Promise<unknown>) => {
    setError(null)
    setBusyId(id)
    try {
      await action()
      await load()
    } catch (err) {
      setError(serverError(err, 'The action failed').message)
    } finally {
      setBusyId(null)
    }
  }

  if (editing) {
    return (
      <TemplateEditor
        template={editing === 'new' ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          load()
        }}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Plan cards</h3>
          <p className="text-sm text-gray-500">Designs for the plans B2C users see on the paywall. Assign one to a plan in the organisation's Plans tab; plans without one use the default card.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5 text-sm text-gray-600">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
          </label>
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="mr-1 h-3.5 w-3.5" /> New template
          </Button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-100 bg-red-50 p-2 text-sm text-red-700">{error}</div>}

      {loading && templates.length === 0 ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl bg-gray-50 py-10 text-center text-gray-500">
          <LayoutTemplate className="mx-auto mb-2 h-10 w-10 text-gray-300" />
          <p>No templates yet</p>
        </div>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {templates.map((template) => {
            const busy = busyId === template.id
            const used = template.planCount > 0
            return (
              <li key={template.id} className={`flex flex-col rounded-xl border border-gray-200 bg-white p-4 ${template.isArchived ? 'opacity-60' : ''}`}>
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-gray-800">{template.name}</div>
                    <div className="text-xs text-gray-500">
                      {VARIANT_NAMES[template.content.variant] ?? template.content.variant}
                      {template.isArchived && ' · archived'}
                      {' · '}
                      {used ? `${template.planCount} plan(s) in ${template.organisationCount} organisation(s)` : 'not used'}
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none mb-4 flex-1 rounded-lg bg-[#F7F9F6] px-6 pb-4 pt-6">
                  <div className="mx-auto max-w-[19rem] origin-top scale-90">
                    <PlanCard plan={SAMPLE_PLANS[0]} card={template.content} language="en" cta={{}} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(template)}>
                    <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => run(template.id, () => authorizedGraphQLRequest(DUPLICATE_TEMPLATE, { id: template.id }))}>
                    <Copy className="mr-1 h-3.5 w-3.5" /> Duplicate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => run(template.id, () => authorizedGraphQLRequest(ARCHIVE_TEMPLATE, { id: template.id, archived: !template.isArchived }))}
                    title={template.isArchived ? 'Offer it again in the Plans tab' : 'Hide it from the Plans tab; plans using it keep it'}
                  >
                    {template.isArchived ? <RotateCcw className="mr-1 h-3.5 w-3.5" /> : <Archive className="mr-1 h-3.5 w-3.5" />}
                    {template.isArchived ? 'Unarchive' : 'Archive'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || used}
                    title={used ? 'In use by plans: archive it instead' : 'Delete'}
                    onClick={() => {
                      if (!confirm(`Delete "${template.name}"?`)) return
                      run(template.id, () => authorizedGraphQLRequest(DELETE_TEMPLATE, { id: template.id }))
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default PlanCardsView
